/**
 * App-wide connectivity signal.
 *
 * Three inputs, in increasing order of trustworthiness:
 *  1. `navigator.onLine` + the `online`/`offline` events — instant, but
 *     `onLine === true` only means "an interface is up", not that the API is
 *     reachable (captive portals, "lie-fi").
 *  2. A `GET /v1/api/ping` confirmation whenever the browser claims to be back
 *     online. That endpoint is deliberately never service-worker cached, so it
 *     always reflects reality. It has three answers, not two: the server
 *     answered, nothing answered, or a security check in front of the server
 *     answered in its place ({@link isBlocked}).
 *  3. Feedback from real requests: {@link reportUnreachable} /
 *     {@link reportReachable}, driven by `useAsyncData`. A network failure
 *     surfaces as `ErpatApiError.status === 0`. A success while
 *     `navigator.onLine` is false came from a cache and is ignored.
 *
 * "Blocked" is a KIND of unreachable, not a third state: `isBlocked` implies
 * `isOffline`, so everything that already waits out an outage (realtime pauses,
 * the 30 s re-probe, the banner) keeps doing so, and all of it recovers on its
 * own the moment the check is lifted. The second flag exists only so the UI can
 * say what is actually wrong instead of "you're offline".
 *
 * Module-level refs rather than a Pinia store, matching token-refresh.ts — this
 * has no need for devtools state and is consumed from plain modules too.
 */
import { readonly, ref, type Ref } from 'vue';
import { systemService } from '@/services/erpat';

/** How long to wait before re-probing while the browser insists it is online. */
const REPROBE_DELAY_MS = 30_000;

const offline = ref(typeof navigator !== 'undefined' ? !navigator.onLine : false);

const blocked = ref(false);

/** True when the API is believed to be unreachable. */
export const isOffline: Readonly<Ref<boolean>> = readonly(offline);

/**
 * True when the API is unreachable because an edge's security check answers in
 * its place (see utils/edge-challenge.ts). Only ever true while
 * {@link isOffline} is — the device's connection is fine, and nothing the user
 * does on their side of it helps.
 */
export const isBlocked: Readonly<Ref<boolean>> = readonly(blocked);

let started = false;
let probeAbort: AbortController | null = null;
let reprobeTimer: ReturnType<typeof setTimeout> | null = null;

async function confirmReachable(): Promise<void> {
  probeAbort?.abort();
  const controller = new AbortController();
  probeAbort = controller;

  try {
    const probe = await systemService.probe(controller.signal);
    if (controller.signal.aborted) return;
    offline.value = probe !== 'online';
    blocked.value = probe === 'blocked';
    // The interface is up but the API isn't answering — keep checking. A
    // blocked API is re-probed the same way: that is what notices the check
    // being lifted.
    if (probe !== 'online' && navigator.onLine) scheduleReprobe();
  } catch {
    /* aborted by a newer probe — that probe owns the state now */
  }
}

function scheduleReprobe(): void {
  if (reprobeTimer) return;
  reprobeTimer = setTimeout(() => {
    reprobeTimer = null;
    void confirmReachable();
  }, REPROBE_DELAY_MS);
}

/** A request failed with `status === 0` while we believed we were online. */
export function reportUnreachable(): void {
  if (offline.value) return;
  void confirmReachable();
}

/**
 * A request succeeded — we are reachable. Unless the browser reports no network
 * interface at all: then the answer came from a cache (the service worker's
 * NetworkFirst routes serve reads from it while offline), which proves nothing.
 * Taken as proof, it hid the offline banner — and its count of punches waiting
 * to sync — behind "You're back online." while the device was still offline.
 * The `online` event and its probe decide the way back instead.
 */
export function reportReachable(): void {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  if (offline.value) offline.value = false;
  if (blocked.value) blocked.value = false;
}

/** Idempotent; called once from App.vue. */
export function startConnectivityWatch(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  window.addEventListener('online', () => void confirmReachable());
  window.addEventListener('offline', () => {
    probeAbort?.abort();
    offline.value = true;
    // No interface at all: whatever was in front of the server, the reason the
    // app is unreachable NOW is the device's own connection.
    blocked.value = false;
  });
}
